/* Field definitions for every register.
 *
 * These are functions of the contract, not constants. A contract that calls
 * its areas of work Stages gets a field labelled Stage, drawn from its own
 * list. A contract with an empty list has no such field at all rather than a
 * dropdown full of another program's vocabulary. Same for the two levels of
 * place: WAPA works by island, VIDE by district and school, and a contract
 * with neither shows neither.
 */

export const STATUS_OPTIONS = {
  deliverable: ["Not Started", "In Progress", "Complete"],
  task: ["Not started", "In progress", "Blocked", "Done"],
  risk: ["Open", "Mitigating", "Closed"],
  issue: ["Open", "Closed"],
  action: ["Open", "In progress", "In review", "On track", "Done"],
  decision: ["Open", "Closed"],
  permit: ["Not Started", "Pending", "In review", "Submitted", "Responded", "Approved", "Completed", "Closed", "N/A"],
  rfi: ["Open", "In review", "Responded", "Closed"],
  compliance: ["Compliant", "At risk", "Non-compliant", "Under review"],
  milestone: ["Planned", "Complete"],
  workstream: ["On Track", "At Risk", "Critical", "Not Started"],
  procurement: ["Open", "Ordered", "Delivered", "Closed"],
};

const PRIORITY = ["Low", "Medium", "High"];
const LEVEL = ["Low", "Medium", "High"];

/* A select that accepts whatever is already in the row.
 * Imported workbooks contain values no dropdown anticipated, including the
 * same word spelled two ways, and a form must never silently rewrite a value
 * it did not offer. */
const openSelect = (key, label, options, extra = {}) => ({
  key, label, type: "select", options, allowOther: true, ...extra,
});

/* The two vocabulary fields, present only when the contract defines them. */
function placeFields(contract) {
  const out = [];
  if (contract?.region_label) {
    out.push({ key: "region", label: contract.region_label, type: "text" });
  }
  if (contract?.site_label) {
    out.push({ key: "site", label: contract.site_label, type: "text" });
  }
  return out;
}

function workstreamField(contract) {
  const list = contract?.workstream_list ?? [];
  if (!list.length) return [];
  return [openSelect("workstream", contract.workstream_label || "Workstream", list)];
}

function stageField(contract) {
  const list = contract?.stage_list ?? [];
  if (!list.length) return [];
  return [openSelect("stage", "Stage", list)];
}

export const REGISTERS = {
  deliverables: {
    label: "Deliverables",
    singular: "deliverable",
    defaultSort: { column: "due_date", ascending: true },
    fields: (c) => [
      { key: "title", label: "Deliverable", type: "text", required: true },
      { key: "vendor", label: "Vendor or responsible party", type: "text" },
      ...stageField(c),
      ...placeFields(c),
      openSelect("deliverable_type", "Type", ["CD", "WP"], {
        hint: "CD is a contract deliverable. WP is a work product.",
      }),
      { key: "due_date", label: "Contractual due date", type: "date" },
      { key: "schedule_date", label: "Schedule of record", type: "date" },
      { key: "date_basis", label: "Date basis", type: "text",
        hint: "How the date was arrived at, such as 30 days after NTP." },
      { key: "completed_date", label: "Completed", type: "date" },
      { key: "percent_complete", label: "Percent complete", type: "number", suffix: "%" },
      openSelect("reported_status", "Status on file", ["Status Not Reported", ...STATUS_OPTIONS.deliverable], {
        hint: "What the vendor reported.",
      }),
      openSelect("status", "Confirmed status", STATUS_OPTIONS.deliverable, {
        hint: "What the program stands behind. Rollups read this one.",
      }),
      { key: "owner", label: "Owner", type: "text" },
      { key: "at_risk_note", label: "Note if at risk", type: "textarea" },
      { key: "notes", label: "Notes", type: "textarea" },
    ],
    columns: ["title", "vendor", "stage", "deliverable_type", "due_date", "percent_complete", "status"],
  },

  risks: {
    label: "Risks",
    singular: "risk",
    defaultSort: { column: "risk_score", ascending: false },
    fields: (c) => [
      { key: "title", label: "Risk", type: "text", required: true },
      { key: "reference", label: "Reference", type: "text",
        hint: "The register's own identifier, if it has one." },
      { key: "category", label: "Category", type: "text" },
      ...workstreamField(c),
      ...placeFields(c),
      { key: "likelihood", label: "Likelihood", type: "number", min: 1, max: 5 },
      { key: "impact", label: "Impact", type: "number", min: 1, max: 5 },
      openSelect("rating", "Overall rating", LEVEL, {
        hint: "For registers that rate a risk rather than scoring it. " +
              "A rating of High counts as high even with no numbers.",
      }),
      openSelect("status", "Status", STATUS_OPTIONS.risk),
      { key: "owner", label: "Owner", type: "text" },
      { key: "consequence", label: "If it happens", type: "textarea",
        hint: "What this costs the program if it lands." },
      { key: "triggers", label: "Triggers and early indicators", type: "textarea" },
      { key: "mitigation", label: "Mitigation", type: "textarea" },
      { key: "assumption", label: "Assumption", type: "textarea" },
      { key: "linked_issue", label: "Issue it has produced", type: "textarea" },
      { key: "decision_log", label: "Decisions taken", type: "textarea" },
      { key: "dependencies", label: "Dependencies", type: "text" },
      { key: "notes", label: "Notes", type: "textarea" },
    ],
    columns: ["title", "category", "site", "likelihood", "impact", "risk_score", "rating", "owner", "status"],
  },

  issues: {
    label: "Issues",
    singular: "issue",
    defaultSort: { column: "due_date", ascending: true },
    fields: (c) => [
      { key: "title", label: "Issue", type: "text", required: true },
      { key: "reference", label: "Reference", type: "text",
        hint: "The log's own identifier, if it has one." },
      ...workstreamField(c),
      ...placeFields(c),
      openSelect("probability", "Probability", LEVEL),
      openSelect("impact", "Impact", LEVEL),
      { key: "owner", label: "Owner", type: "text" },
      { key: "due_date", label: "Due", type: "date" },
      openSelect("status", "Status", STATUS_OPTIONS.issue),
      { key: "consequence", label: "If it is not resolved", type: "textarea" },
      { key: "mitigation", label: "Mitigation", type: "textarea" },
      { key: "latest_update", label: "Latest update", type: "textarea" },
      { key: "notes", label: "Notes", type: "textarea" },
    ],
    columns: ["title", "reference", "workstream", "impact", "owner", "due_date", "status"],
  },

  actions: {
    label: "Action items",
    singular: "action item",
    defaultSort: { column: "due_date", ascending: true },
    fields: (c) => [
      { key: "title", label: "Action", type: "text", required: true },
      /* The team's own number. Not a key and not unique: the VIDE log has two
         items numbered 1, and it is what somebody says out loud on a call. */
      { key: "reference", label: "Item number", type: "text" },
      ...workstreamField(c),
      ...placeFields(c),
      { key: "owner", label: "Owner", type: "text" },
      { key: "opened_date", label: "Raised", type: "date" },
      { key: "due_date", label: "Due", type: "date" },
      { key: "closed_date", label: "Closed", type: "date" },
      openSelect("priority", "Priority", PRIORITY),
      openSelect("status", "Status", STATUS_OPTIONS.action),
      /* Which meeting raised it. The most useful cut of an action log there
         is, and the one no register had until the VIDE log arrived. */
      { key: "source", label: "Raised at", type: "text" },
      { key: "notes", label: "Notes", type: "textarea" },
    ],
    columns: ["reference", "title", "owner", "due_date", "status", "source"],
  },

  decisions: {
    label: "Decisions",
    singular: "decision",
    defaultSort: { column: "date", ascending: false },
    fields: () => [
      { key: "title", label: "Decision", type: "text", required: true },
      { key: "reference", label: "Reference", type: "text" },
      { key: "date", label: "Date", type: "date" },
      { key: "owner", label: "Owner", type: "text" },
      openSelect("priority", "Priority", PRIORITY),
      openSelect("status", "Status", STATUS_OPTIONS.decision),
      { key: "waiting_on", label: "Waiting on", type: "text",
        hint: "Whose call this is: a named leader, the client, a sub. Powers the decisions-pending queue." },
      { key: "cost_of_delay", label: "Cost of delay", type: "text",
        hint: "What a day of delay costs, in your words. Sorts the queue by consequence." },
      { key: "notes", label: "Impact and detail", type: "textarea" },
    ],
    columns: ["title", "date", "owner", "priority", "status"],
  },

  tasks: {
    label: "Tasks",
    singular: "task",
    defaultSort: { column: "due_date", ascending: true },
    fields: (c) => [
      { key: "title", label: "Task", type: "text", required: true },
      ...workstreamField(c),
      ...placeFields(c),
      { key: "owner", label: "Owner", type: "text" },
      { key: "start_date", label: "Start", type: "date" },
      { key: "due_date", label: "Due", type: "date" },
      openSelect("priority", "Priority", PRIORITY),
      openSelect("status", "Status", STATUS_OPTIONS.task),
      { key: "notes", label: "Notes", type: "textarea" },
    ],
    columns: ["title", "workstream", "owner", "due_date", "priority", "status"],
  },

  permits: {
    label: "Permits",
    singular: "permit",
    defaultSort: { column: "submitted", ascending: false },
    fields: (c) => [
      { key: "title", label: "Permit or approval", type: "text", required: true },
      { key: "ref_number", label: "Reference", type: "text" },
      { key: "category", label: "Permit category", type: "text" },
      { key: "issuing_agency", label: "Issuing agency", type: "text" },
      ...placeFields(c),
      openSelect("risk_level", "Risk level", LEVEL),
      openSelect("impact_level", "Impact level", LEVEL),
      { key: "timeline_days", label: "Expected turnaround", type: "text",
        hint: "As written, so a range like 60-90 is fine." },
      { key: "prerequisites", label: "Prerequisites", type: "textarea" },
      { key: "contractor", label: "Contractor", type: "text" },
      { key: "reviewer", label: "Reviewer or contact", type: "text" },
      { key: "submitted", label: "Submitted", type: "date" },
      { key: "response_due_date", label: "Response due", type: "date" },
      { key: "response_date", label: "Responded", type: "date" },
      { key: "closed_date", label: "Closed", type: "date" },
      openSelect("status", "Status", STATUS_OPTIONS.permit),
      { key: "notes", label: "Notes", type: "textarea" },
    ],
    columns: ["title", "site", "issuing_agency", "timeline_days", "submitted", "status"],
  },

  rfis: {
    label: "RFIs",
    singular: "RFI",
    defaultSort: { column: "submitted", ascending: false },
    fields: (c) => [
      { key: "title", label: "Subject", type: "text", required: true },
      { key: "ref_number", label: "RFI number", type: "text" },
      { key: "rfi_type", label: "Type", type: "text" },
      ...placeFields(c),
      { key: "contract_number", label: "Contract number", type: "text" },
      { key: "submitted_by_name", label: "Submitted by", type: "text" },
      { key: "submitted_by_company", label: "Company", type: "text" },
      { key: "submitted_to", label: "Submitted to", type: "text" },
      openSelect("priority", "Priority", PRIORITY),
      { key: "submitted", label: "Submitted", type: "date" },
      { key: "received_date", label: "Received", type: "date" },
      { key: "response_due_date", label: "Response due", type: "date" },
      { key: "response_received_date", label: "Response received", type: "date" },
      { key: "closed_date", label: "Closed", type: "date" },
      openSelect("status", "Status", STATUS_OPTIONS.rfi),
      { key: "cost_impact", label: "Cost impact", type: "boolean" },
      { key: "schedule_impact", label: "Schedule impact", type: "boolean" },
      { key: "notes", label: "Notes", type: "textarea" },
    ],
    columns: ["title", "ref_number", "site", "submitted", "response_due_date", "status"],
  },

  compliance_items: {
    label: "Compliance",
    singular: "requirement",
    defaultSort: { column: "title", ascending: true },
    fields: () => [
      { key: "title", label: "Requirement", type: "text", required: true },
      { key: "source", label: "Clause or source", type: "text" },
      openSelect("status", "Status", STATUS_OPTIONS.compliance),
      { key: "owner", label: "Owner", type: "text" },
      { key: "notes", label: "Notes", type: "textarea" },
    ],
    columns: ["title", "source", "owner", "status"],
  },

  resources: {
    label: "Resources",
    singular: "resource",
    defaultSort: { column: "title", ascending: true },
    fields: () => [
      { key: "title", label: "Name", type: "text", required: true },
      { key: "role", label: "Labour category", type: "text" },
      { key: "rate", label: "Hourly rate", type: "number", prefix: "$" },
      { key: "hours_planned", label: "Hours planned", type: "number" },
      { key: "hours_used", label: "Hours used", type: "number" },
      { key: "allocation_pct", label: "Allocation to this project", type: "number", suffix: "%",
        hint: "What share of this person's working time is committed here, 0 to 100. Entered, not computed: the app cannot see their other programs." },
      { key: "notes", label: "Notes", type: "textarea" },
    ],
    columns: ["title", "role", "allocation_pct", "rate", "hours_planned", "hours_used"],
  },

  vendors: {
    label: "Vendors",
    singular: "vendor",
    defaultSort: { column: "title", ascending: true },
    fields: () => [
      { key: "title", label: "Vendor", type: "text", required: true },
      { key: "terms_days", label: "Payment terms", type: "number", suffix: "days",
        hint: "Invoices from this vendor age against this unless the invoice states its own." },
    ],
    columns: ["title", "terms_days"],
  },

  invoices: {
    label: "Invoices",
    singular: "invoice",
    defaultSort: { column: "submitted", ascending: false },
    fields: () => [
      { key: "title", label: "Invoice number", type: "text", required: true },
      { key: "vendor", label: "Vendor", type: "text" },
      { key: "description", label: "Description", type: "text" },
      { key: "submitted", label: "Submitted", type: "date" },
      { key: "amount", label: "Amount", type: "number", prefix: "$" },
      { key: "terms", label: "Payment terms", type: "number", suffix: "days" },
      { key: "paid_date", label: "Paid", type: "date" },
    ],
    columns: ["title", "vendor", "submitted", "amount", "terms", "paid_date"],
  },

  expenses: {
    label: "Expenses",
    singular: "expense",
    defaultSort: { column: "date", ascending: false },
    fields: () => [
      { key: "title", label: "Description", type: "text", required: true },
      { key: "category", label: "Category", type: "text" },
      { key: "date", label: "Date", type: "date" },
      { key: "amount", label: "Amount", type: "number", prefix: "$" },
    ],
    columns: ["title", "category", "date", "amount"],
  },

  procurement: {
    label: "Procurement",
    singular: "order",
    defaultSort: { column: "key_date", ascending: true },
    fields: (c) => [
      { key: "title", label: "Scope", type: "text", required: true },
      { key: "reference", label: "Contract or PO number", type: "text" },
      { key: "vendor", label: "Vendor", type: "text" },
      ...placeFields(c),
      { key: "tie_to", label: "Ties to", type: "text" },
      { key: "value", label: "Value", type: "number", prefix: "$" },
      { key: "value_note", label: "Value as written", type: "text",
        hint: "The register's own wording, where the figure sits inside a larger NTE." },
      { key: "key_date", label: "Key date", type: "date" },
      { key: "key_date_note", label: "Dates as written", type: "text" },
      openSelect("status", "Status", STATUS_OPTIONS.procurement),
      { key: "status_note", label: "What to confirm", type: "textarea" },
      { key: "source", label: "Source", type: "text" },
      { key: "notes", label: "Notes", type: "textarea" },
    ],
    columns: ["title", "reference", "vendor", "value", "key_date", "status", "status_note"],
  },

  milestones: {
    label: "Milestones",
    singular: "milestone",
    defaultSort: { column: "date", ascending: true },
    fields: () => [
      { key: "title", label: "Milestone", type: "text", required: true },
      { key: "date", label: "Date", type: "date" },
      { key: "percent_complete", label: "Percent complete", type: "number", suffix: "%" },
      openSelect("status", "Status", STATUS_OPTIONS.milestone),
    ],
    columns: ["title", "date", "percent_complete", "status"],
  },

  schedule_stages: {
    label: "Schedule of record",
    singular: "activity",
    defaultSort: { column: "start_date", ascending: true },
    fields: (c) => [
      { key: "title", label: "Activity", type: "text", required: true },
      { key: "activity_id", label: "Activity ID", type: "text",
        hint: "The id the scheduling tool uses, such as PH1.1030." },
      { key: "outline_level", label: "Outline level", type: "number", min: 1, max: 12,
        hint: "1 is a phase, 2 sits under it, and so on. Both tools export this." },
      ...workstreamField(c),
      { key: "start_date", label: "Start", type: "date" },
      { key: "finish_date", label: "Finish", type: "date" },
      { key: "start_is_actual", label: "Start is actual", type: "boolean" },
      { key: "finish_is_actual", label: "Finish is actual", type: "boolean" },
      { key: "percent_complete", label: "Percent complete", type: "number", suffix: "%" },
      { key: "owner", label: "Owner", type: "text" },
      { key: "notes", label: "Notes", type: "textarea" },
    ],
    columns: ["title", "activity_id", "start_date", "finish_date",
              "percent_complete", "owner"],
  },

  workstream_status: {
    label: "Status board",
    singular: "entry",
    fields: (c) => [
      ...(c?.workstream_list?.length
        ? [openSelect("title", c.workstream_label || "Workstream", c.workstream_list, { required: true })]
        : [{ key: "title", label: c?.workstream_label || "Workstream", type: "text", required: true }]),
      openSelect("status", "Status", STATUS_OPTIONS.workstream),
      /* The month this status describes. Without it, writing September's
         status destroys August's and August's report starts showing
         September's words. */
      { key: "period", label: "Month", type: "text", placeholder: "2026-08",
        hint: "The month this status describes, as YYYY-MM. Each month gets its own rows so last month's report keeps saying what it said." },
      { key: "comment", label: "Key comments", type: "textarea" },
    ],
    columns: ["period", "title", "status", "comment"],
    defaultSort: { column: "period", ascending: false },
  },

  key_terms: {
    label: "Key terms",
    singular: "term",
    defaultSort: { column: "title", ascending: true },
    fields: () => [
      { key: "title", label: "Term", type: "text", required: true },
      { key: "notes", label: "Detail", type: "textarea" },
    ],
    columns: ["title", "notes"],
  },
};

export const registerFields = (key, contract) => REGISTERS[key].fields(contract);

export const registerColumns = (key, contract) => {
  const defined = registerFields(key, contract);
  const byKey = new Map(defined.map((f) => [f.key, f]));
  const cols = REGISTERS[key].columns
    .map((k) => byKey.get(k) ?? syntheticColumn(k))
    .filter(Boolean);
  return cols;
};

/* Columns the database computes rather than the form collecting. */
function syntheticColumn(key) {
  if (key === "risk_score") return { key, label: "Score", type: "number", readOnly: true };
  return null;
}
